/**
 * useEventModalUrl
 *
 * Hook that synchronizes the EventDetailModal state with the browser URL.
 * Enables deep linking so users can share links to specific calendars/events.
 *
 * URL format:
 * - ?calendar=formula-1 → Opens calendar overview
 * - ?calendar=formula-1&event=monaco-gp-2026 → Opens specific event
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEventModalStore } from '../stores/useEventModalStore';

export const useEventModalUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const isUpdatingFromUrl = useRef(false);

  // Get store state and actions
  const isOpen = useEventModalStore((state) => state.isOpen);
  const currentCalendar = useEventModalStore((state) => state.currentCalendar);
  const currentEvent = useEventModalStore((state) => state.currentEvent);
  const currentIndex = useEventModalStore((state) => state.currentIndex);
  const openCalendarOverview = useEventModalStore((state) => state.openCalendarOverview);
  const openModal = useEventModalStore((state) => state.openModal);
  const closeModal = useEventModalStore((state) => state.closeModal);

  // On mount: Check URL params and open modal if needed
  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    const calendarId = searchParams.get('calendar');
    const eventId = searchParams.get('event');

    if (calendarId) {
      isUpdatingFromUrl.current = true;

      if (eventId) {
        // Open to specific event
        openModal(calendarId, eventId);
      } else {
        // Open to calendar overview
        openCalendarOverview(calendarId);
      }

      // Reset flag after a tick
      setTimeout(() => {
        isUpdatingFromUrl.current = false;
      }, 100);
    }
  }, []); // Only run on mount

  // Sync URL when modal state changes
  useEffect(() => {
    // Skip if we're updating from URL (to prevent infinite loops)
    if (isUpdatingFromUrl.current) return;

    if (isOpen && currentCalendar) {
      const newParams = new URLSearchParams(searchParams);

      // Set calendar param
      newParams.set('calendar', currentCalendar.id);

      // Set or remove event param based on current view
      if (currentEvent && currentEvent.id) {
        newParams.set('event', currentEvent.id);
      } else {
        // On overview page (-1) or event without id
        newParams.delete('event');
      }

      // Update URL without triggering navigation
      setSearchParams(newParams, { replace: true });
    } else if (!isOpen) {
      // Modal closed - remove calendar/event params
      const newParams = new URLSearchParams(searchParams);
      const hadCalendarParam = newParams.has('calendar');

      newParams.delete('calendar');
      newParams.delete('event');

      // Only update if we had params to remove
      if (hadCalendarParam) {
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [isOpen, currentCalendar?.id, currentEvent?.id, currentIndex]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const calendarId = params.get('calendar');
      const eventId = params.get('event');

      isUpdatingFromUrl.current = true;

      if (calendarId) {
        if (eventId) {
          openModal(calendarId, eventId);
        } else {
          openCalendarOverview(calendarId);
        }
      } else if (isOpen) {
        closeModal();
      }

      setTimeout(() => {
        isUpdatingFromUrl.current = false;
      }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, openCalendarOverview, openModal, closeModal]);
};

export default useEventModalUrl;
